import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Firestore, doc, onSnapshot, setDoc, updateDoc, getDoc, deleteDoc } from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { ToastrService } from 'ngx-toastr';

export interface CallData {
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  candidates?: RTCIceCandidateInit[];
  providerEmail?: string;
  clientEmail?: string;
  clientUid?: string;
  providerUid?: string;
  status?: 'incoming' | 'accepted' | 'rejected' | 'ended';
  timestamp?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CallService {
  private callActiveSubject = new BehaviorSubject<boolean>(false);
  public callActive$ = this.callActiveSubject.asObservable();

  private callDataSubject = new BehaviorSubject<CallData | null>(null);
  public callData$ = this.callDataSubject.asObservable();

  private callUnsubscribe: any = null;
  private currentUserId: string = '';

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private toastr: ToastrService
  ) {
    this.authService.userSubject.subscribe(user => {
      if (user) {
        this.currentUserId = user.uid;
        this.setupCallListener(user.uid);
      } else {
        this.cleanup();
      }
    });
  }

  private setupCallListener(userId: string): void {
    console.log('Setting up call listener for user:', userId);

    if (this.callUnsubscribe) {
      this.callUnsubscribe();
    }

    const receiverCallDocRef = doc(this.firestore, `calls/${userId}`);

    this.callUnsubscribe = onSnapshot(receiverCallDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const callData = snapshot.data() as CallData;
        console.log('Call data received:', callData);

        this.callDataSubject.next(callData);

        // Xử lý trạng thái call
        if (callData.status === 'ended' || callData.status === 'rejected') {
          console.log('Call ended/rejected, updating status');
          this.callActiveSubject.next(false);

          // Delay một chút rồi mới clear data để component có thể xử lý
          setTimeout(() => {
            this.callDataSubject.next(null);
          }, 1000);
        } else {
          const isActive = callData.status === 'accepted' || callData.status === 'incoming';
          this.callActiveSubject.next(isActive);
        }

        // Show notifications
        if (callData.status === 'accepted' && callData.providerUid === userId) {
          this.toastr.success('Cuộc gọi được chấp nhận', 'Thành công');
        } else if (callData.status === 'rejected' && callData.providerUid === userId) {
          this.toastr.info('Cuộc gọi bị từ chối', 'Thông báo');
        } else if (callData.status === 'ended') {
          this.toastr.info('Cuộc gọi đã kết thúc', 'Thông báo');
        }
      } else {
        console.log('No call document found');
        this.callActiveSubject.next(false);
        this.callDataSubject.next(null);
      }
    });
  }


  private setupCallerListener(): void {
    // Sẽ được setup khi initiate call
  }

  private listenToReceiverResponse(receiverUid: string): void {
    console.log('Setting up caller listener for receiver:', receiverUid);

    const receiverCallDocRef = doc(this.firestore, `calls/${receiverUid}`);

    // Unsubscribe existing listener nếu có
    if (this.callUnsubscribe) {
      this.callUnsubscribe();
    }

    this.callUnsubscribe = onSnapshot(receiverCallDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const callData = snapshot.data() as CallData;
        console.log('Caller received response from receiver:', callData);

        // Chỉ process nếu current user là provider (caller)
        if (callData.providerUid === this.currentUserId) {
          this.callDataSubject.next(callData);

          const isActive = callData.status !== 'ended' && callData.status !== 'rejected';
          this.callActiveSubject.next(isActive);

          if (callData.status === 'accepted') {
            this.toastr.success('Cuộc gọi được chấp nhận', 'Thành công');
          } else if (callData.status === 'rejected') {
            this.toastr.info('Cuộc gọi bị từ chối', 'Thông báo');
          }
        }
      }
    });
  }

  public async initiateCall(receiverUid: string, receiverEmail: string): Promise<boolean> {
    const currentUser = this.authService.userSubject.value;
    if (!currentUser || !receiverUid) {
      this.toastr.error('Không thể thực hiện cuộc gọi', 'Lỗi');
      return false;
    }

    try {
      // Chỉ tạo document với ID của receiver
      const callDocRef = doc(this.firestore, `calls/${receiverUid}`);

      // Check if there's already an active call
      const callSnapshot = await getDoc(callDocRef);
      if (callSnapshot.exists()) {
        const existingCall = callSnapshot.data() as CallData;
        if (existingCall.status === 'incoming' || existingCall.status === 'accepted') {
          this.toastr.warning('Người dùng đang trong cuộc gọi khác', 'Không thể gọi');
          return false;
        }
      }

      const callData: CallData = {
        providerUid: currentUser.uid,
        providerEmail: currentUser.email || '',
        clientUid: receiverUid,
        clientEmail: receiverEmail,
        status: 'incoming',
        timestamp: Date.now()
      };

      await setDoc(callDocRef, callData);

      // Setup listener để nhận response từ receiver
      this.listenToReceiverResponse(receiverUid);

      this.callActiveSubject.next(true);
      this.callDataSubject.next(callData);

      return true;
    } catch (error) {
      console.error('Error initiating call:', error);
      this.toastr.error('Không thể khởi tạo cuộc gọi', 'Lỗi');
      return false;
    }
  }

  public async updateCallData(callData: Partial<CallData>, docUid?: string): Promise<void> {
    const currentCallData = this.callDataSubject.value;
    if (!currentCallData) return;

    try {
      // Luôn update vào document của clientUid (receiver)
      const callDocId = docUid || currentCallData.clientUid || '';
      if (!callDocId) return;

      const callDocRef = doc(this.firestore, `calls/${callDocId}`);

      // Merge ICE candidates nếu có
      if (callData.candidates) {
        const snap = await getDoc(callDocRef);
        let existingCandidates: RTCIceCandidateInit[] = [];
        if (snap.exists()) {
          const data = snap.data() as CallData;
          existingCandidates = data.candidates || [];
        }
        callData.candidates = [...existingCandidates, ...callData.candidates];
      }

      await updateDoc(callDocRef, callData);
      console.log('Call data updated in document:', callDocId, callData);

    } catch (error) {
      console.error('Error updating call data:', error);
    }
  }

  public async endCall(docUid?: string): Promise<void> {
    const callData = this.callDataSubject.value;
    if (!callData) return;

    try {
      // Luôn update vào document của clientUid (receiver)
      const callDocId = docUid || callData.clientUid || '';
      if (!callDocId) return;

      const callDocRef = doc(this.firestore, `calls/${callDocId}`);

      // Chỉ update status nếu document vẫn tồn tại
      const docSnapshot = await getDoc(callDocRef);
      if (docSnapshot.exists()) {
        await updateDoc(callDocRef, { status: 'ended' });
        console.log('Call status updated to ended');
      }

      // Clean up after delay
      setTimeout(async () => {
        try {
          const docCheck = await getDoc(callDocRef);
          if (docCheck.exists()) {
            await deleteDoc(callDocRef);
            console.log('Call document deleted');
          }
        } catch (error) {
          console.error('Error cleaning up call document:', error);
        }
      }, 3000); // Giảm thời gian delay

    } catch (error) {
      console.error('Error ending call:', error);
    }

    // Reset local state
    this.callActiveSubject.next(false);
    this.callDataSubject.next(null);
  }


  public isCallActive(): boolean {
    return this.callActiveSubject.value;
  }

  public getCurrentCallData(): CallData | null {
    return this.callDataSubject.value;
  }

  private cleanup(): void {
    if (this.callUnsubscribe) {
      this.callUnsubscribe();
      this.callUnsubscribe = null;
    }
    this.callActiveSubject.next(false);
    this.callDataSubject.next(null);
  }
}